import { forwardRef, useEffect, useMemo, useState } from "react";
import { BLOOD_TEST_VALIDATION_ENDPOINT } from "../../api/endpoints";
import {
    submitRestBloodTestData,
    submitExerciseBloodTestData,
} from "../../api/forms/blood-test";
import {
    convertBloodTestApiResponsesToFormValues,
    convertBloodTestFormValuesToSeparateObjects,
} from "../../api/forms/blood-test/converters";
import { BloodTestParameterType } from "../../api/forms/blood-test/types";
import { Nullable } from "../../api/types";
import { fetcher } from "../../api/utils";
import useDataInputForm, {
    FORM_IDS,
    FormDataTypes,
} from "../../hooks/useDataInputForm";
import useImperativeFormSubmit from "../../hooks/useImperativeFormSubmit";
import useTestId from "../../hooks/useTestId";
import { FormHandle, InputTypes, NumericValidation } from "../../types";
import Error from "../Error";
import DataInputGenericForm, {
    DataInputGenericFormProps,
} from "./DataInputGenericForm";

export default forwardRef<
    FormHandle<Nullable<FormDataTypes["BLOOD_TEST"]>> | null,
    {}
>(function BloodTestForm(
    props,
    ref: React.ForwardedRef<FormHandle<
        Nullable<FormDataTypes["BLOOD_TEST"]>
    > | null>
) {
    const { formData, loading, error, setFromData } = useDataInputForm({
        formId: FORM_IDS.BLOOD_TEST,
    });

    const testId = useTestId();

    const [formValues, setFormValues] = useState<
        FormDataTypes["BLOOD_TEST"] | null | undefined
    >();

    const [validationErrors, setValidationErrors] = useState<
        Partial<
            Record<
                keyof FormDataTypes["BLOOD_TEST"],
                { primary?: string; secondary?: string }
            >
        >
    >({});

    useEffect(() => {
        if (formData) {
            setFormValues(formData);
        }
    }, [formData, setFormValues]);

    const submitBothBloodTestData = async ({
        testId,
        values,
    }: {
        testId: string;
        values: FormDataTypes["BLOOD_TEST"];
    }): Promise<FormDataTypes["BLOOD_TEST"]> => {
        // Transform the combined data into separate rest and exercise data
        const { restData, exerciseData } =
            convertBloodTestFormValuesToSeparateObjects(values);

        // Submit both rest and exercise data
        const [restResponse, exerciseResponse] = await Promise.all([
            submitRestBloodTestData({ testId, values: restData }),
            submitExerciseBloodTestData({ testId, values: exerciseData }),
        ]);

        const updatedValues: FormDataTypes["BLOOD_TEST"] =
            convertBloodTestApiResponsesToFormValues(
                restResponse,
                exerciseResponse
            );
        return updatedValues;
    };

    const { submissionLoading } = useImperativeFormSubmit<
        FormDataTypes["BLOOD_TEST"]
    >({
        formValues,
        formData,
        ref,
        onSubmitSuccess: setFromData,
        submitFunction: submitBothBloodTestData,
    });

    type FromFields = DataInputGenericFormProps<
        FormDataTypes["BLOOD_TEST"]
    >["formFields"];

    const formFields: Partial<
        Record<BloodTestParameterType, { validation: NumericValidation }>
    > = {
        pao2: { validation: { decimalPlaces: 1 } },
        paco2: { validation: { decimalPlaces: 1 } },
        ph: { validation: { decimalPlaces: 2 } },
        lactate: {
            validation: { decimalPlaces: 1 },
        },
        hemoglobin: {
            validation: { decimalPlaces: 2 },
        },
    };

    const formFieldsData = useMemo<FromFields>((): FromFields => {
        if (!formData || !formValues) return [];
        return Object.keys(formFields).map((fieldKey) => {
            const formatVal = (value: number | null | "-") => {
                return typeof value === "number" ? value : null;
            };
            const _fieldKey = fieldKey as keyof typeof formFields;
            const restValues = formValues[_fieldKey].rest;
            const restVal = formatVal(restValues.measured);
            const exerciseVal = formatVal(
                formValues[_fieldKey].exercise.measured
            );

            const fieldConfig = {
                id: fieldKey,
                label: restValues.label,
                type: InputTypes.NUMBER,
                value: restVal,
                originalValue: restVal,
                secondaryValue: exerciseVal,
                withSecondaryValue: true,
                isSecondaryInputEditable: true,
                required: false,
                error: validationErrors?.[_fieldKey]?.primary,
                secondaryError: validationErrors?.[_fieldKey]?.secondary,
                validation: formFields[_fieldKey]?.validation,
                isSmallInput: true,
            } satisfies FromFields[number];
            return fieldConfig;
        });
    }, [formData, formValues, validationErrors]);

    if (error) {
        return <Error />;
    }

    const onFromValuesChange = async (
        {
            value,
            fieldSettings: { id: fieldId },
        }: {
            value: Nullable<string | number>;
            fieldSettings: FromFields[number];
        },
        inputLevel: "primary" | "secondary" = "primary"
    ) => {
        const valueToSave =
            value === null || isNaN(Number(value))
                ? null // if not a number, don't update the value
                : Number(value);

        const validationRes = await fetcher(
            `${BLOOD_TEST_VALIDATION_ENDPOINT}?test_id=${testId}`,
            {
                method: "POST",
                body: JSON.stringify({
                    [fieldId]: { measured: valueToSave },
                }),
                headers: {
                    "Content-Type": "application/json",
                },
            },
            true
        );

        const errorMessage = validationRes?.[fieldId]?.error_message;

        if (!validationRes || errorMessage) {
            setValidationErrors((prev) => ({
                ...prev,
                [fieldId]: {
                    ...prev[fieldId as keyof typeof prev],
                    [inputLevel]: errorMessage,
                },
            }));
        } else {
            setValidationErrors((prev) => ({
                ...prev,
                [fieldId]: {
                    ...prev[fieldId as keyof typeof prev],
                    [inputLevel]: undefined,
                },
            }));
        }

        setFormValues((prevFormValues) => {
            if (!prevFormValues || !formData) {
                return prevFormValues;
            }
            const inputLevelKey =
                inputLevel === "secondary" ? "exercise" : "rest";
            const fieldValues =
                prevFormValues[fieldId as BloodTestParameterType];
            return {
                ...prevFormValues,
                [fieldId]: {
                    ...fieldValues,
                    [inputLevelKey]: {
                        ...fieldValues[inputLevelKey],
                        measured: valueToSave,
                    },
                },
            };
        });
    };

    return (
        <DataInputGenericForm
            formTitle="Lab Results"
            formFields={formFieldsData}
            formData={formValues}
            onFromValuesChange={onFromValuesChange}
            loading={loading || submissionLoading}
            headers={["Parameter", "Rest", "Exercise"]}
            classes={{ table: "w-80 xl:w-[450px]" }}
        />
    );
});
